import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import Icon from './Icon';
import BrandLogo from './BrandLogo';
import { Button } from './DesignSystem';
import { getGuestNavItems, getNavItemsForDesktopHeader, getNavItemsForDesktopDropdown } from '../utils/navigationUtils';
import { getUserInitials as getInitials, getUserDisplayName } from '../utils/userDisplay';

const Header: React.FC<{ userRole?: string }> = memo(({ userRole: propUserRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token, user, logout } = useAuth();
  const { getThemeStyles } = useTheme();
  const themeStyles = getThemeStyles();

  const {
    notifications,
    unreadCount: notifUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const [showSettings, setShowSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const roleFromRoute = !token ? 'guest' : undefined;
  const effectiveRole = (user?.role as string | undefined) || propUserRole || roleFromRoute;

  const primaryItems = token && user ? getNavItemsForDesktopHeader(effectiveRole) : [];
  const secondaryItems = getNavItemsForDesktopDropdown(effectiveRole);

  const toggleSettings = useCallback(() => {
    setShowSettings((prev) => !prev);
    setShowNotifications(false);
  }, []);

  const toggleNotifications = useCallback(() => {
    setShowNotifications((prev) => !prev);
    setShowSettings(false);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsRef.current && !settingsRef.current.contains(event.target as Node)) {
        setShowSettings(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showSettings || showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSettings, showNotifications]);

  const isActive = (path: string) => location.pathname === path;

  const headerContent = (
    <header
      className="header-desktop"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1001,
        minHeight: 'var(--header-height)',
        padding: '0.65rem clamp(1rem, 3vw, 2.2rem)',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        alignItems: 'center',
        gap: '1rem',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,250,252,0.96) 100%)',
        borderBottom: `1px solid ${themeStyles.border}`,
        boxShadow: '0 10px 26px rgba(15, 23, 42, 0.08)',
        backdropFilter: 'blur(10px)'
      }}
    >
      <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 10 }}>
        <BrandLogo height={74} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#0f766e',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            whiteSpace: 'nowrap'
          }}
        >
          GDPR ready
        </span>
      </Link>

      {token && user ? (
        <nav
          className="header-primary-nav"
          aria-label="Navigazione primaria"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none'
          }}
        >
          {primaryItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              style={{
                border: isActive(item.path) ? '1px solid transparent' : `1px solid ${themeStyles.border}`,
                background: isActive(item.path)
                  ? 'linear-gradient(120deg, #0f766e 0%, #0ea5e9 100%)'
                  : '#ffffff',
                color: isActive(item.path) ? '#ffffff' : themeStyles.textSecondary,
                borderRadius: 12,
                padding: '9px 12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                fontWeight: 700,
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: isActive(item.path) ? '0 8px 18px rgba(14, 165, 233, 0.24)' : '0 2px 8px rgba(15,23,42,0.05)'
              }}
            >
              <Icon name={item.icon} size={16} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      ) : (
        <nav style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          {getGuestNavItems().map((item) => (
            <Link key={item.path} to={item.path} style={{ textDecoration: 'none' }}>
              <Button variant="secondary" size="sm">{item.label}</Button>
            </Link>
          ))}
        </nav>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10 }}>
        {token && user ? (
          <>
            <div style={{ position: 'relative' }} ref={notificationsRef}>
              <button
                aria-label="Apri notifiche"
                onClick={toggleNotifications}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${themeStyles.border}`,
                  background: '#ffffff',
                  color: '#0f172a',
                  cursor: 'pointer'
                }}
              >
                <Icon name="alert" size={18} />
              </button>
              {notifUnreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    minWidth: 18,
                    height: 18,
                    borderRadius: 999,
                    padding: '0 5px',
                    background: '#ef4444',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                </span>
              )}

              {showNotifications && (
                <div
                  style={{
                    position: 'absolute',
                    top: '112%',
                    right: 0,
                    width: 360,
                    maxHeight: 460,
                    overflow: 'auto',
                    background: '#ffffff',
                    border: `1px solid ${themeStyles.border}`,
                    borderRadius: 16,
                    boxShadow: themeStyles.cardShadow,
                    zIndex: 1300
                  }}
                >
                  <div
                    style={{
                      padding: '10px 12px',
                      borderBottom: `1px solid ${themeStyles.border}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <strong style={{ color: themeStyles.text }}>Notifiche</strong>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="secondary" size="sm" onClick={() => markAllAsRead?.()}>Segna tutte</Button>
                      <Button variant="secondary" size="sm" onClick={() => navigate('/notifiche')}>Apri pagina</Button>
                    </div>
                  </div>

                  {(notifications || []).slice(0, 6).map((n) => (
                    <div
                      key={n.id}
                      style={{
                        padding: '11px 12px',
                        borderBottom: `1px solid ${themeStyles.border}`,
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 8
                      }}
                    >
                      <div>
                        <div style={{ color: themeStyles.text, fontWeight: n.read ? 600 : 700, marginBottom: 4 }}>{n.title}</div>
                        <div style={{ color: themeStyles.textSecondary, fontSize: 13 }}>{n.message}</div>
                        <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                          {!n.read && (
                            <Button variant="secondary" size="sm" onClick={() => markAsRead(n.id)}>
                              Segna
                            </Button>
                          )}
                          <Button variant="secondary" size="sm" onClick={() => deleteNotification(n.id)}>
                            Elimina
                          </Button>
                        </div>
                      </div>
                      <span style={{ fontSize: 11, color: themeStyles.textSecondary, whiteSpace: 'nowrap' }}>
                        {new Date(n.created_at).toLocaleDateString('it-IT')}
                      </span>
                    </div>
                  ))}

                  {(notifications || []).length === 0 && (
                    <div style={{ padding: 16, textAlign: 'center', color: themeStyles.textSecondary }}>
                      Nessuna notifica
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              aria-label="Profilo"
              onClick={() => navigate('/profile')}
              style={{
                width: 42,
                height: 42,
                borderRadius: 14,
                border: 'none',
                background: 'linear-gradient(130deg, #0f766e 0%, #0ea5e9 100%)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: themeStyles.glow
              }}
            >
              {getInitials(user, user)}
            </button>

            <div ref={settingsRef} style={{ position: 'relative' }}>
              <button
                aria-label="Apri menu"
                onClick={toggleSettings}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  border: `1px solid ${themeStyles.border}`,
                  background: '#ffffff',
                  color: '#0f172a',
                  cursor: 'pointer'
                }}
              >
                <Icon name="hamburger" size={22} />
              </button>

              {showSettings && (
                <div
                  style={{
                    position: 'absolute',
                    top: '112%',
                    right: 0,
                    width: 310,
                    background: '#ffffff',
                    border: `1px solid ${themeStyles.border}`,
                    borderRadius: 18,
                    boxShadow: themeStyles.cardShadow,
                    overflow: 'hidden',
                    zIndex: 1200
                  }}
                >
                  <div
                    style={{
                      padding: '14px 14px 12px',
                      borderBottom: `1px solid ${themeStyles.border}`,
                      background: 'linear-gradient(130deg, #0f766e 0%, #0ea5e9 100%)',
                      color: '#ffffff'
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{getUserDisplayName(user, user)}</div>
                    <div style={{ opacity: 0.9, fontSize: 12 }}>{effectiveRole}</div>
                  </div>

                  <div style={{ padding: 10, display: 'grid', gap: 8 }}>
                    {secondaryItems.map((item) => (
                      <button
                        key={item.path}
                        onClick={() => {
                          navigate(item.path);
                          setShowSettings(false);
                        }}
                        style={{
                          width: '100%',
                          border: `1px solid ${themeStyles.border}`,
                          background: '#ffffff',
                          color: themeStyles.text,
                          borderRadius: 12,
                          padding: '10px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          fontWeight: 600,
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <Icon name={item.icon} size={18} />
                        <span>{item.label}</span>
                      </button>
                    ))}

                    <button
                      onClick={() => {
                        handleLogout();
                        setShowSettings(false);
                      }}
                      style={{
                        width: '100%',
                        border: '1px solid #dc2626',
                        background: '#dc2626',
                        color: '#ffffff',
                        borderRadius: 12,
                        padding: '10px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        fontWeight: 700,
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <Icon name="logout" size={18} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <Link to="/login" style={{ textDecoration: 'none' }}>
            <Button variant="primary" size="md">Accedi / Registrati</Button>
          </Link>
        )}
      </div>
    </header>
  );

  if (typeof document === 'undefined') {
    return headerContent;
  }

  return createPortal(headerContent, document.body);
});

Header.displayName = 'Header';

export default Header;
